import { useLazyGetEnergyDataSpecificQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { usePutEnergyInitiativeMutation } from '@/api/recalcV2/endpoints/energyInitiativesApiSlice';
import { useEffect, useMemo, useState } from 'react';

import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';
import EnergySumChartInteractive from '../highcharts/EnergySumChartInteractive';

import ToolMenu from './InitiativeGraphTool';

import { showSnackbar } from '@/redux/snackbarSlice';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import DateRangeWithGenerate from './DateRangeWithGenerate';

const ZONE = 'Europe/Copenhagen';
const steps = [
  'Navn & beskrivelse',
  'Vælg datakilder',
  'Vælg baseline periode',
  'Vælg sammenlignings periode',
];

const fmt = (ms) =>
  typeof ms === 'number'
    ? DateTime.fromMillis(ms, { zone: ZONE }).toLocaleString(
        DateTime.DATETIME_SHORT,
      )
    : '—';

const asOption = (n) => ({
  id: n?.energyTreeNodeId ?? n?.id ?? n,
  type: n?.type,
  label:
    n?.name ?? n?.displayName ?? n?.title ?? String(n?.energyTreeNodeId ?? n),
});

const toUtcIso = (v) => {
  if (v == null) return null;
  if (v && typeof v === 'object' && typeof v.toMillis === 'function') {
    return DateTime.fromMillis(v.toMillis(), { zone: 'utc' }).toISO();
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return DateTime.fromMillis(v, { zone: 'utc' }).toISO();
  }
  if (typeof v === 'string') {
    const d = DateTime.fromISO(v, { zone: 'utc' });
    return d.isValid ? d.toISO() : null;
  }
  return null;
};

const fromIsoToLuxon = (iso) => {
  if (!iso) return null;
  const d = DateTime.fromISO(iso).setZone(ZONE);
  return d.isValid ? d : null;
};

function deriveState(ini) {
  if (
    ini.baselineStart &&
    ini.baselineEnd &&
    ini.comparisonStart &&
    ini.comparisonEnd
  )
    return 'Done';
  if (ini.baselineStart && ini.baselineEnd && ini.comparisonStart)
    return 'Active';
  if (ini.baselineStart && ini.baselineEnd) return 'Initialized';
  if (ini.baselineStart) return 'Initialized';
  return 'Created';
}

/**
 * EditInitiativeWithPlotDialog with Stepper (3 steps)
 */
const EditInitiativeWithPlotDialog = ({
  open,
  onClose,
  energyGroup,
  initiative,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const [activeStep, setActiveStep] = useState(0);
  const [useManualInputs, setUseManualInputs] = useState(false);
  const [didInitialFetch, setDidInitialFetch] = useState(false);

  // Prepare defaults from the existing initiative
  const initialBaselineStart = fromIsoToLuxon(initiative?.baselineStart);
  const initialBaselineEnd = fromIsoToLuxon(initiative?.baselineEnd);
  const initialComparisonStart = fromIsoToLuxon(initiative?.comparisonStart);
  const initialComparisonEnd = fromIsoToLuxon(initiative?.comparisonEnd);

  const now = DateTime.now().setZone(ZONE);

  // Time window shown above the chart (span both baseline & comparison if present)
  const spanStarts = [initialBaselineStart, initialComparisonStart]
    .filter(Boolean)
    .map((d) => d.toMillis());
  const spanEnds = [initialBaselineEnd, initialComparisonEnd]
    .filter(Boolean)
    .map((d) => d.toMillis());

  const defaultStartDt =
    spanStarts.length > 0
      ? DateTime.fromMillis(Math.min(...spanStarts)).startOf('day')
      : now.minus({ days: 7 }).startOf('day');

  const defaultEndDt =
    spanEnds.length > 0
      ? DateTime.fromMillis(Math.max(...spanEnds)).endOf('day')
      : now.endOf('day');

  // Form state (prefilled)
  const [formState, setFormState] = useState({
    name: initiative?.name ?? '',
    description: initiative?.description ?? '',
    state: initiative?.state ?? null,
    completed: initiative?.completed ?? null,
    baselineStart: initialBaselineStart ?? null,
    baselineEnd: initialBaselineEnd ?? null,
    comparisonStart: initialComparisonStart ?? null,
    comparisonEnd: initialComparisonEnd ?? null,
    preferredModel: initiative?.preferredModel ?? 0,
    energyTreeNodes: Array.isArray(initiative?.energyTreeNodes)
      ? initiative.energyTreeNodes
      : [],
    energyGroupId: energyGroup?.energyGroupId || null,
  });

  const [saveInitiative, { isLoading: isSaving }] =
    usePutEnergyInitiativeMutation();

  // Node selection mode
  const [sourceSelectionMode, setNodeSelectionMode] = useState('node');

  // Node options
  const nodeOptions = useMemo(() => {
    const src = energyGroup?.energyTreeNodes ?? [];
    return src.map(asOption).filter((o) => o.id);
  }, [energyGroup]);

  // Selected nodes as option objects (derive from IDs)
  const [selectedNodes, setSelectedNodes] = useState(() =>
    nodeOptions.filter((o) => (formState.energyTreeNodes || []).includes(o.id)),
  );
  useEffect(() => {
    setSelectedNodes(
      nodeOptions.filter((o) =>
        (formState.energyTreeNodes || []).includes(o.id),
      ),
    );
  }, [nodeOptions, formState.energyTreeNodes]);

  // Group nodes by type for SEU mode
  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    energyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [energyGroup]);

  const seuTypes = useMemo(
    () => Array.from(nodeGroupsByType.keys()),
    [nodeGroupsByType],
  );

  // Luxon time range for the fetch (defaults based on existing initiative)
  const [startDt, setStartDt] = useState(defaultStartDt);
  const [endDt, setEndDt] = useState(defaultEndDt);
  const startMs = startDt?.toMillis();
  const endMs = endDt?.toMillis();
  const isRangeValid =
    typeof startMs === 'number' && typeof endMs === 'number' && endMs > startMs;

  // Initial fetch time window: from earliest baseline start (or default) to latest known end
  const initialFetchStartMs = useMemo(() => {
    const bS = fromIsoToLuxon(initiative?.baselineStart);
    const start = (bS ? bS.startOf('day') : defaultStartDt)?.toMillis();
    return typeof start === 'number' ? start : null;
  }, [initiative?.baselineStart, defaultStartDt]);

  const initialFetchEndMs = useMemo(() => {
    const ends = [
      fromIsoToLuxon(initiative?.baselineEnd),
      fromIsoToLuxon(initiative?.comparisonEnd),
      defaultEndDt,
    ]
      .filter(Boolean)
      .map((d) => d.endOf('day').toMillis());

    if (!ends.length) return null;
    const end = Math.max(...ends);
    return Number.isFinite(end) ? end : null;
  }, [initiative?.baselineEnd, initiative?.comparisonEnd, defaultEndDt]);

  // Energy data query
  const [fetchEnergyData, { data, isFetching, isLoading, isError, error }] =
    useLazyGetEnergyDataSpecificQuery();

  // One-time initial fetch on open
  useEffect(() => {
    if (!open) return;
    if (didInitialFetch) return;

    const node_ids =
      (Array.isArray(initiative?.energyTreeNodes) &&
      initiative.energyTreeNodes.length
        ? initiative.energyTreeNodes
        : formState.energyTreeNodes) || [];

    const validRange =
      typeof initialFetchStartMs === 'number' &&
      typeof initialFetchEndMs === 'number' &&
      initialFetchEndMs > initialFetchStartMs;

    if (!energyGroup?.energyGroupId || !node_ids.length || !validRange) return;

    fetchEnergyData({
      accountId: energyGroup.accountId,
      energyGroupId: energyGroup.energyGroupId,
      start: initialFetchStartMs,
      end: initialFetchEndMs,
      node_ids,
    });

    setDidInitialFetch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    didInitialFetch,
    energyGroup?.energyGroupId,
    energyGroup?.accountId,
    initiative?.energyTreeNodes,
    formState.energyTreeNodes,
    initialFetchStartMs,
    initialFetchEndMs,
  ]);

  // Reset the one-time flag when closing or switching initiative
  useEffect(() => {
    if (!open) setDidInitialFetch(false);
  }, [open]);
  useEffect(() => {
    setDidInitialFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative?.energyInitiativeId || initiative?.id]);

  const handleGenerate = () => {
    const node_ids = selectedNodes.map((n) => n.id);
    if (!energyGroup?.energyGroupId || node_ids.length === 0 || !isRangeValid)
      return;

    fetchEnergyData({
      accountId: energyGroup?.accountId,
      energyGroupId: energyGroup?.energyGroupId,
      start: startMs,
      end: endMs,
      node_ids,
    });
  };

  const pointsForChart = useMemo(
    () => (Array.isArray(data?.EnergyData) ? data.EnergyData : []),
    [data],
  );

  // Interactive selections (pre-fill from initiative)
  const [activeTool, setActiveTool] = useState(null);
  const [selections, setSelections] = useState({
    baseline: {
      start: formState.baselineStart,
      end: formState.baselineEnd,
    },
    comparison: {
      start: formState.comparisonStart,
      end: formState.comparisonEnd,
    },
  });

  const clearBaseline = () => setSelections((s) => ({ ...s, baseline: {} }));
  const clearComparison = () =>
    setSelections((s) => ({ ...s, comparison: {} }));
  const clearAll = () => setSelections({ baseline: {}, comparison: {} });

  useEffect(() => {
    if (activeStep === 2) {
      setActiveTool('baseline-point');
    } else if (activeStep === 3) {
      setActiveTool('comparison-point');
    } else {
      setActiveTool(null);
    }
  }, [activeStep]);

  // Reflect selections back to form state
  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      baselineStart: selections.baseline.start
        ? DateTime.fromMillis(selections.baseline.start, { zone: ZONE })
        : null,
      baselineEnd: selections.baseline.end
        ? DateTime.fromMillis(selections.baseline.end, { zone: ZONE })
        : null,
      comparisonStart: selections.comparison.start
        ? DateTime.fromMillis(selections.comparison.start, { zone: ZONE })
        : null,
      comparisonEnd: selections.comparison.end
        ? DateTime.fromMillis(selections.comparison.end, { zone: ZONE })
        : null,
    }));
  }, [selections]);

  // Rehydrate when initiative changes
  useEffect(() => {
    if (!initiative) return;
    const bS = fromIsoToLuxon(initiative?.baselineStart);
    const bE = fromIsoToLuxon(initiative?.baselineEnd);
    const cS = fromIsoToLuxon(initiative?.comparisonStart);
    const cE = fromIsoToLuxon(initiative?.comparisonEnd);

    setFormState({
      name: initiative?.name ?? '',
      description: initiative?.description ?? '',
      state: initiative?.state ?? null,
      completed: initiative?.completed ?? null,
      baselineStart: bS ?? null,
      baselineEnd: bE ?? null,
      comparisonStart: cS ?? null,
      comparisonEnd: cE ?? null,
      preferredModel: initiative?.preferredModel ?? 0,
      energyTreeNodes: Array.isArray(initiative?.energyTreeNodes)
        ? initiative.energyTreeNodes
        : [],
      energyGroupId: energyGroup?.energyGroupId || null,
    });

    setSelections({
      baseline: {
        start: bS ? bS.toMillis() : null,
        end: bE ? bE.toMillis() : null,
      },
      comparison: {
        start: cS ? cS.toMillis() : null,
        end: cE ? cE.toMillis() : null,
      },
    });

    const spanStartMs = [bS, cS].filter(Boolean).map((d) => d.toMillis());
    const spanEndMs = [bE, cE].filter(Boolean).map((d) => d.toMillis());
    if (spanStartMs.length && spanEndMs.length) {
      setStartDt(
        DateTime.fromMillis(Math.min(...spanStartMs))
          .setZone(ZONE)
          .startOf('day'),
      );
      setEndDt(
        DateTime.fromMillis(Math.max(...spanEndMs))
          .setZone(ZONE)
          .endOf('day'),
      );
    }

    setSelectedNodes(
      (energyGroup?.energyTreeNodes ?? [])
        .map(asOption)
        .filter((o) => (initiative?.energyTreeNodes ?? []).includes(o.id)),
    );
    setActiveStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative, open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const canGenerate =
    energyGroup?.energyGroupId && selectedNodes.length > 0 && isRangeValid;

  const handleSave = async () => {
    const payload = {
      ...formState,
      baselineStart: toUtcIso(formState.baselineStart),
      baselineEnd: toUtcIso(formState.baselineEnd),
      comparisonStart: toUtcIso(formState.comparisonStart),
      comparisonEnd: toUtcIso(formState.comparisonEnd),
      state: deriveState(formState),
    };

    try {
      await saveInitiative({
        accountId: energyGroup?.accountId,
        energyInitiativeId: initiative?.id || initiative?.energyInitiativeId,
        updates: payload,
      }).unwrap();

      dispatch(
        showSnackbar({ message: 'Initiativ opdateret', severity: 'success' }),
      );
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under opdatering af initiativ',
          severity: 'error',
          error: err?.data?.message || err?.error || 'Unknown error',
        }),
      );
    }
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  // --- Step validation like the Create form ---
  const step1Valid = Boolean(formState.name?.trim());
  const step2Valid = formState.energyTreeNodes.length > 0;
  const isBaselineSet =
    formState.baselineStart !== null && formState.baselineEnd !== null;

  const renderStep1 = () => (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <TextField
        label={t('add_initiative_form.name.label')}
        name="name"
        variant="filled"
        size="small"
        required
        error={!formState.name}
        value={formState.name}
        onChange={handleInputChange}
        fullWidth
        sx={{
          '& .MuiFilledInput-root': {
            '&:before': { borderBottom: 'none' },
            '&:after': { borderBottom: 'none' },
            '&:hover:before': { borderBottom: 'none !important' },
            borderRadius: 1,
          },
        }}
      />
      <TextField
        label={t('add_initiative_form.description.label')}
        name="description"
        variant="filled"
        size="small"
        value={formState.description}
        onChange={handleInputChange}
        fullWidth
        multiline
        sx={{
          mb: 2,
          '& .MuiFilledInput-root': {
            '&:before': { borderBottom: 'none' },
            '&:after': { borderBottom: 'none' },
            '&:hover:before': { borderBottom: 'none !important' },
            borderRadius: 1,
          },
        }}
      />
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={2}>
      <ToggleButtonGroup
        value={sourceSelectionMode}
        fullWidth
        size="small"
        exclusive
        onChange={(_, newMode) => {
          if (newMode !== null) setNodeSelectionMode(newMode);
        }}
        sx={{ alignSelf: 'flex-start', mr: 1 }}
      >
        <ToggleButton value="node">
          <ListItemText primary="Vælg individuelle noder" />
        </ToggleButton>
        <ToggleButton value="seu">
          <ListItemText primary="Vælg efter type (SEU)" />
        </ToggleButton>
      </ToggleButtonGroup>

      {sourceSelectionMode === 'node' && (
        <Autocomplete
          multiple
          options={nodeOptions}
          size="small"
          value={selectedNodes}
          onChange={(_, val) => {
            setSelectedNodes(val);
            setFormState((prev) => ({
              ...prev,
              energyTreeNodes: val.map((v) => v.id),
            }));
          }}
          disableCloseOnSelect
          filterSelectedOptions
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          sx={{ flex: 2, minWidth: 260 }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{
                  backgroundColor: getTypeColor(option?.type),
                  position: 'relative',
                  borderRadius: '25px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: `1px solid divider`,
                  marginRight: 1,
                }}
              >
                <i
                  style={{ color: 'white' }}
                  className={`fa-solid ${getTypeIcon(option?.type)}`}
                />
              </Box>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('button_labels.select_nodes')}
              variant="filled"
              fullWidth
              required
              error={!formState.energyTreeNodes.length}
              helperText={t('button_labels.select_nodes_helper_text')}
              sx={{
                '& .MuiFilledInput-root': {
                  '&:before': { borderBottom: 'none' },
                  '&:after': { borderBottom: 'none' },
                  '&:hover:before': { borderBottom: 'none !important' },
                  borderRadius: 1,
                  '& .MuiFilledInput-input': { margin: '18px 18px' },
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={<Typography>{option.label}</Typography>}
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: getTypeColor(option.type),
                  color: 'white',
                  borderRadius: 2,
                }}
              />
            ))
          }
        />
      )}

      {sourceSelectionMode === 'seu' && (
        <Autocomplete
          multiple
          options={seuTypes}
          getOptionLabel={(type) => type} // keep it simple for label logic
          value={Array.from(
            new Set(
              formState.energyTreeNodes
                .map((id) =>
                  (energyGroup?.energyTreeNodes || []).find(
                    (n) => n.energyTreeNodeId === id,
                  ),
                )
                .filter(Boolean)
                .map((node) => node.type),
            ),
          )}
          onChange={(_, newSelectedTypes) => {
            const selectedNodeIds = newSelectedTypes
              .flatMap((type) => nodeGroupsByType.get(type) || [])
              .map((node) => node.energyTreeNodeId);

            setSelectedNodes(
              nodeOptions.filter((o) => selectedNodeIds.includes(o.id)),
            );
            setFormState((prev) => ({
              ...prev,
              energyTreeNodes: selectedNodeIds,
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('button_labels.select_seu')}
              helperText={t('button_labels.select_seu_helper_text')}
              variant="filled"
              placeholder={t('button_labels.select_seu')}
              required
              fullWidth
              error={!formState.energyTreeNodes.length}
              sx={{
                '& .MuiFilledInput-root': {
                  '&:before': { borderBottom: 'none' },
                  '&:after': { borderBottom: 'none' },
                  '&:hover:before': { borderBottom: 'none !important' },
                  borderRadius: 1,
                  '& .MuiFilledInput-input': { margin: '14px 14px' },
                },
              }}
            />
          )}
          renderOption={(props, type) => {
            const allOfType = nodeGroupsByType.get(type) || [];
            const selectedCount = allOfType.filter((node) =>
              formState.energyTreeNodes.includes(node.energyTreeNodeId),
            ).length;

            return (
              <li {...props} key={type}>
                <Box
                  sx={{
                    backgroundColor: getTypeColor(type),
                    position: 'relative',
                    borderRadius: '25px',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: `1px solid divider`,
                    marginRight: 1,
                  }}
                >
                  <i
                    style={{ color: 'white' }}
                    className={`fa-solid ${getTypeIcon(type)}`}
                  />
                </Box>
                <Typography sx={{ flexGrow: 1 }}>
                  {type} ({selectedCount}/{allOfType.length})
                </Typography>
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((type, index) => {
              const allOfType = nodeGroupsByType.get(type) || [];
              const selectedCount = allOfType.filter((node) =>
                formState.energyTreeNodes.includes(node.energyTreeNodeId),
              ).length;
              const percentage = Math.round(
                (selectedCount / allOfType.length) * 100,
              );
              const color = getTypeColor(type);

              return (
                <Box
                  key={type}
                  sx={{ position: 'relative', display: 'inline-block', mr: 1 }}
                >
                  <Chip
                    label={
                      <Typography>
                        {type} {selectedCount}/{allOfType.length}
                      </Typography>
                    }
                    color={
                      selectedCount === allOfType.length ? 'success' : 'warning'
                    }
                    variant="filled"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: color,
                      color: 'white',
                    }}
                    {...getTagProps({ index })}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      left: 0,
                      right: 0,
                      height: 3,
                      borderRadius: 1,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          selectedCount === allOfType.length
                            ? '#2e7d32'
                            : '#ed6c02',
                      },
                    }}
                  />
                </Box>
              );
            })
          }
        />
      )}
    </Stack>
  );

  const renderStep3 = () => (
    <Stack spacing={2}>
      {!isRangeValid && (
        <Typography variant="caption" color="error">
          Please pick a valid time range (End must be after Start).
        </Typography>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={useManualInputs}
            onChange={() => setUseManualInputs(!useManualInputs)}
          />
        }
        label="Brug manuelle input felter"
      />

      {useManualInputs && (
        <>
          <Typography variant="subtitle2" color="textSecondary">
            Angiv baseline periode
          </Typography>
          <DateRangePicker
            startText={t('add_initiative_form.baseline_period.start_label')}
            endText={t('add_initiative_form.baseline_period.end_label')}
            value={[formState.baselineStart, formState.baselineEnd]}
            variant="filled"
            onChange={(newValue) => {
              setFormState((prev) => ({
                ...prev,
                baselineStart: newValue[0],
                baselineEnd: newValue[1],
              }));
            }}
            renderInput={(startProps, endProps) => (
              <>
                <TextField
                  {...startProps}
                  variant="outlined"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 1,
                    },
                  }}
                  fullWidth
                />
                <TextField
                  {...endProps}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 1,
                    },
                  }}
                  fullWidth
                />
              </>
            )}
          />
        </>
      )}

      {!useManualInputs && (
        <Card variant="outlined" sx={{ p: 2, minHeight: 420 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Chip
              variant="filled"
              label={`Baselineperiode: ${fmt(selections.baseline.start)} → ${fmt(selections.baseline.end)}`}
              onDelete={clearBaseline}
              sx={{
                height: 'auto',
                borderRadius: 2,
                color: 'info.main',
                backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1),
              }}
            />
            <DateRangeWithGenerate
              startDt={startDt}
              endDt={endDt}
              canGenerate={canGenerate}
              onChangeRange={(newStart, newEnd) => {
                setStartDt(newStart.setZone(ZONE));
                setEndDt(newEnd.setZone(ZONE));
              }}
              onGenerate={(startMs, endMs) => {
                const node_ids = selectedNodes.map((n) => n.id);
                if (!energyGroup?.energyGroupId || node_ids.length === 0)
                  return;

                fetchEnergyData({
                  accountId: energyGroup?.accountId,
                  energyGroupId: energyGroup?.energyGroupId,
                  start: startMs,
                  end: endMs,
                  node_ids,
                });
              }}
              zone={ZONE}
              locale="da"
              generateLabel="Generer graf"
            />
          </Stack>

          {!data && !isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400, opacity: 0.7 }}
              spacing={1}
            >
              <Typography variant="body1">
                Adjust nodes or dates, then click{' '}
                <strong>Generate graph</strong>.
              </Typography>
              {!energyGroup?.energyTreeNodes?.length && (
                <Typography variant="body2">
                  No nodes found in this energy group.
                </Typography>
              )}
            </Stack>
          )}

          {isFetching && (
            <div
              style={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Loading…
            </div>
          )}

          {isError && (
            <div
              style={{
                height: 400,
                color: '#b00020',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            >
              Failed to load data
              {error?.data ? `: ${JSON.stringify(error.data)}` : ''}
            </div>
          )}

          {!isFetching &&
            !isError &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length > 0 && (
              <EnergySumChartInteractive
                points={pointsForChart}
                selections={selections}
                activeTool={activeTool}
                onChange={setSelections}
                title=""
                height={380}
              />
            )}

          {!isFetching &&
            !isError &&
            data &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length === 0 && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: 400, opacity: 0.7 }}
              >
                <Typography>
                  No data returned for the chosen nodes and period.
                </Typography>
              </Stack>
            )}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent={'center'}
          >
            <ToolMenu
              activeTool={activeTool}
              activeStep={activeStep}
              disabled={!canGenerate}
              setActiveTool={setActiveTool}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );

  const renderStep4 = () => (
    <Stack spacing={2}>
      {!isRangeValid && (
        <Typography variant="caption" color="error">
          Please pick a valid time range (End must be after Start).
        </Typography>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={useManualInputs}
            onChange={() => setUseManualInputs(!useManualInputs)}
          />
        }
        label="Brug manuelle input felter"
      />

      {useManualInputs && (
        <>
          <Typography variant="subtitle2" color="textSecondary">
            Angiv sammenlignings periode
          </Typography>
          <DateRangePicker
            startText="Start"
            endText="Slut"
            value={[formState.comparisonStart, formState.comparisonEnd]}
            onChange={(newValue) => {
              setFormState((prev) => ({
                ...prev,
                comparisonStart: newValue[0],
                comparisonEnd: newValue[1],
              }));
            }}
            renderInput={(startProps, endProps) => (
              <>
                <TextField
                  {...startProps}
                  variant="filled"
                  helperText="Sammenligningsperioden skal ligge efter baseline perioden"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 2,
                    },
                  }}
                  fullWidth
                />
                <TextField
                  {...endProps}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 2,
                    },
                  }}
                  fullWidth
                />
              </>
            )}
          />
        </>
      )}
      {!useManualInputs && (
        <Card variant="outlined" sx={{ p: 2, minHeight: 420 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Chip
              variant="filled"
              onDelete={clearComparison}
              label={`Sammenligningsperiode: ${fmt(selections.comparison.start)} → ${fmt(selections.comparison.end)}`}
              sx={{
                height: 'auto',
                borderRadius: 2,
                color: 'success.main',
                backgroundColor: (theme) =>
                  alpha(theme.palette.success.main, 0.1),
              }}
            />
            <DateRangeWithGenerate
              startDt={startDt}
              endDt={endDt}
              canGenerate={canGenerate}
              onChangeRange={(newStart, newEnd) => {
                setStartDt(newStart.setZone(ZONE));
                setEndDt(newEnd.setZone(ZONE));
              }}
              onGenerate={() => {
                handleGenerate();
              }}
              zone={ZONE}
              locale="da"
              generateLabel="Generer graf"
            />
          </Stack>

          {!data && !isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400, opacity: 0.7 }}
              spacing={1}
            >
              <Typography variant="body1">
                Efter du har valgt datakilder for initiativet, skal du klikke på{' '}
                <strong>Generer graf</strong> som findes ved at åbne
                datovælgeren oppe til højre, og en tidsperiode som initiatives
                foretages.
              </Typography>
              {!energyGroup?.energyTreeNodes?.length && (
                <Typography variant="body2">
                  Der er ikke fundet nogen noder i denne energigruppe.
                </Typography>
              )}
              {activeStep === 2 || activeStep === 3 ? (
                <Typography variant="caption" color="text.secondary">
                  Tip: Du kan også bruge de manuelle input felter ved at skifte
                  "Brug manuelle input felter" til.
                </Typography>
              ) : null}
            </Stack>
          )}

          {isFetching && (
            <div
              style={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Loading…
            </div>
          )}

          {isError && (
            <div
              style={{
                height: 400,
                color: '#b00020',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            >
              Failed to load data
              {error?.data ? `: ${JSON.stringify(error.data)}` : ''}
            </div>
          )}

          {!isFetching &&
            !isError &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length > 0 && (
              <EnergySumChartInteractive
                points={pointsForChart}
                selections={selections}
                activeTool={activeTool}
                onChange={setSelections}
                title=""
                height={380}
              />
            )}

          {!isFetching &&
            !isError &&
            data &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length === 0 && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: 400, opacity: 0.7 }}
              >
                <Typography>
                  No data returned for the chosen nodes and period.
                </Typography>
              </Stack>
            )}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent={'center'}
          >
            <ToolMenu
              activeTool={activeTool}
              activeStep={activeStep}
              disabled={!canGenerate}
              setActiveTool={setActiveTool}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );

  const handleNext = () => {
    if (activeStep === 0 && !step1Valid) return;
    if (activeStep === 1 && !step2Valid) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={
        (activeStep === 2 && !useManualInputs) ||
        (activeStep === 3 && !useManualInputs)
          ? 'xl'
          : 'md'
      }
      fullWidth
    >
      <DialogTitle>Rediger initiativ</DialogTitle>

      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Stack spacing={2}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepIcon-root.Mui-completed': {
                        color: 'success.main',
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: 'text.secondary',
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        color: 'text.primary',
                        fontSize: '1.1rem',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && renderStep1()}
            {activeStep === 1 && renderStep2()}
            {activeStep === 2 && renderStep3()}
            {activeStep === 3 && renderStep4()}
            {activeStep === 2 || activeStep === 3 ? (
              <Typography variant="caption" color="text.secondary">
                Tip: Brug værktøjsmenuen under grafen til at vælge og justere
                baseline- og sammenligningsperioder. Du kan også bruge de
                manuelle input felter ved at skifte "Brug manuelle input felter"
                til.
              </Typography>
            ) : null}
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Button onClick={handleClose} disabled={isSaving}>
            Afbryd
          </Button>
        </Box>
        <Box>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ mr: 1 }}
          >
            Tilbage
          </Button>

          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !step1Valid) ||
                (activeStep === 1 && !step2Valid) ||
                ((activeStep === 2 || activeStep === 3) && !isBaselineSet)
              }
            >
              Næste
            </Button>
          )}

          {(activeStep === steps.length - 1 || activeStep === 2) && (
            <Button
              sx={{ ml: 1 }}
              onClick={handleSave}
              variant="contained"
              disabled={
                isSaving || !formState.name || !formState.energyTreeNodes.length
              }
            >
              Gem ændringer
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditInitiativeWithPlotDialog;
